import { z } from 'zod';
import { FLOOR_PLAN_KIND_ORDER } from '@obraplan/core/domain/takeoff/floor-plan';
import { MEASURE_BY_LABEL, type MeasureBy } from '@obraplan/core/domain/takeoff/geometry';
import { APPLIES_TO_LABEL, type AppliesTo } from '@obraplan/core/domain/takeoff/applies-to';
import { ROOM_KIND_LABEL, type RoomKind } from '@obraplan/core/domain/takeoff/room';
import { OPENING_KIND_LABEL, type OpeningKind } from '@obraplan/core/domain/takeoff/wall';
import { ITEM_KIND_LABEL, type ItemKind } from '@obraplan/core/domain/takeoff/takeoff-line';
import { SHEET_SCOPE_LABEL, type SheetScope } from '@obraplan/core/domain/takeoff/service-catalog';
import { STEEL_GAUGES, type SteelGauge } from '@obraplan/core/domain/takeoff/steel';
import {
  ELEMENT_ORIENTATIONS,
  STRUCTURAL_ELEMENT_KIND_LABEL,
  type AdvanceSide,
  type ElementOrientation,
  type StructuralElementKind,
} from '@obraplan/core/domain/takeoff/structural-element';
import type { PieceKind } from '@obraplan/core/domain/takeoff/piece-execution';
import type { PieceShape } from '@obraplan/core/domain/takeoff/piece-shape';
import type { Point } from '@obraplan/core/domain/takeoff/polygon';
import type { RoomWeightBasis } from '@obraplan/core/domain/takeoff/room-quantity';
import type {
  ImportedPieceKind,
  ImportedPieceStatus,
} from '@obraplan/core/domain/takeoff/imported-piece';
import { businessDateSchema, idSchema, nonEmptyString, optionalTrimmed } from './common';

/**
 * Entrada do quantitativo — a espinha (Fase 14, fatia 1).
 *
 * O tipo de pavimento e o de tipologia vêm do domínio, e não de literais repetidas aqui:
 * acrescentar `MEZANINO` a `FLOOR_PLAN_KIND_ORDER` passa a valer na validação no mesmo
 * instante, sem uma segunda lista para alguém esquecer de atualizar.
 */

const floorPlanKindSchema = z.enum(
  FLOOR_PLAN_KIND_ORDER as unknown as [string, ...string[]],
) as z.ZodType<(typeof FLOOR_PLAN_KIND_ORDER)[number]>;

// ── Pavimento tipo ──────────────────────────────────────────────────────────────

export const createFloorPlanSchema = z.object({
  projectId: idSchema,
  name: nonEmptyString('Nome', 160),
  kind: floorPlanKindSchema,
  code: optionalTrimmed(30),
  notes: optionalTrimmed(500),
});
export type CreateFloorPlanRequest = z.infer<typeof createFloorPlanSchema>;

export const renameFloorPlanSchema = z.object({
  projectId: idSchema,
  floorPlanId: idSchema,
  name: nonEmptyString('Nome', 160),
});

export const deleteFloorPlanSchema = z.object({
  projectId: idSchema,
  floorPlanId: idSchema,
});

// ── Tipologia ───────────────────────────────────────────────────────────────────

export const createUnitTypeSchema = z.object({
  projectId: idSchema,
  name: nonEmptyString('Nome', 160),
  kind: z.enum(['APARTAMENTO', 'VAGA', 'AREA_COMUM']),
  code: optionalTrimmed(30),
  notes: optionalTrimmed(500),
  /**
   * O desenho em que ela nasce, e o rótulo da posição dentro dele.
   *
   * Vêm juntos de propósito: tipologia sem posição não aparece em pavimento nenhum, e
   * criar as duas em chamadas separadas deixaria a primeira órfã se a segunda falhasse.
   * Ausentes, a tipologia é cadastrada solta — é o caminho de quem ainda vai decidir
   * onde ela mora.
   */
  floorPlanId: idSchema.optional(),
  positionLabel: optionalTrimmed(60),
  /**
   * Quantas vezes a posição existe no pavimento — a garagem com quarenta vagas.
   *
   * Ausente é uma, que é todo apartamento e toda área comum: eles existem um por posição
   * porque a divisa entre dois aptos precisa ser de alguém (RN-163). Vaga não tem essa
   * necessidade, e quarenta cadastros para dizer "quarenta" seriam trabalho por nada.
   */
  positionCount: z.number().int().min(1).max(1000).optional(),
});
export type CreateUnitTypeRequest = z.infer<typeof createUnitTypeSchema>;

export const renameUnitTypeSchema = z.object({
  projectId: idSchema,
  unitTypeId: idSchema,
  name: nonEmptyString('Nome', 160),
});

/**
 * A escolha inteira, e não a diferença — o mesmo motivo do papel: duas abas abertas sobre
 * a mesma tipologia mandando "some esta, tire aquela" produziriam uma mistura que nenhuma
 * das duas viu.
 */
export const setUnitTypeServicesSchema = z.object({
  projectId: idSchema,
  unitTypeId: idSchema,
  serviceIds: z
    .array(idSchema)
    .max(2000)
    .transform((ids) => [...new Set(ids)]),
});

export const deleteUnitTypeSchema = z.object({
  projectId: idSchema,
  unitTypeId: idSchema,
});

// ── O vínculo pavimento real → desenho ──────────────────────────────────────────

export const linkFloorToFloorPlanSchema = z.object({
  projectId: idSchema,
  floorId: idSchema,
  /** Ausente desliga o vínculo: o pavimento continua, e sai do quantitativo. */
  floorPlanId: idSchema.optional(),
});

// ── A geometria ─────────────────────────────────────────────────────────────────

/**
 * O desenho inteiro de uma obra, num envelope só (Fase 14, fatia 3).
 *
 * **Não são vinte e cinco contratos, um por ação da tela.** A geometria é internamente
 * amarrada — a parede aponta dois ambientes, o trecho aponta parede e elemento, casar duas
 * paredes apaga uma e reescreve a outra — e gravá-la em pedaços deixaria o servidor com
 * meia montagem entre uma requisição e a seguinte: parede apontando ambiente que ainda não
 * chegou, trecho apontando parede que acabou de sumir.
 *
 * Por isso o envelope, e por isso a gravação é **substituição**: o que não veio, saiu. É a
 * mesma semântica que o rascunho do navegador sempre teve, e é a que a tela já pratica.
 *
 * O preço está declarado: **o último a gravar vence**. Duas pessoas montando a mesma
 * tipologia ao mesmo tempo perdem o trabalho de uma delas, sem aviso. Aceito nesta fatia
 * porque o desenho é trabalho de uma pessoa por vez — quem monta a tipologia senta uma
 * tarde e monta —, e resolver conflito de montagem exigiria versionar cada peça, que é
 * fase própria.
 *
 * Carimbo de tempo **não vem do cliente**: `createdAt` é preservado do que já está gravado
 * e `updatedAt` sai do relógio do servidor. Aceitar o do navegador seria aceitar a hora
 * errada de um tablet com o relógio adiantado, e ela apareceria na auditoria.
 */

const medida = z.number().finite().positive();
const medidaOuZero = z.number().finite().nonnegative();

const pontoSchema = z.object({ x: z.number().finite(), y: z.number().finite() });

const doDominio = <T extends string>(valores: readonly string[]): z.ZodType<T> =>
  z.enum(valores as unknown as [string, ...string[]]) as unknown as z.ZodType<T>;

const roomKindSchema = doDominio<RoomKind>(Object.keys(ROOM_KIND_LABEL));
const openingKindSchema = doDominio<OpeningKind>(Object.keys(OPENING_KIND_LABEL));
const elementKindSchema = doDominio<StructuralElementKind>(
  Object.keys(STRUCTURAL_ELEMENT_KIND_LABEL),
);
const orientationSchema = doDominio<ElementOrientation>(ELEMENT_ORIENTATIONS);
const advanceSideSchema = doDominio<AdvanceSide>(['A', 'B', 'AMBOS']);

/**
 * O fator próprio de um item na peça ou no cômodo (Fase 39, etapa 3, RN-511, RN-512). Fator não
 * positivo e item repetido são recusados no domínio (`checkItemFactors`), onde a regra já mora.
 */
export const itemFactorInputSchema = z.object({
  serviceKey: z.string().min(1).max(400),
  itemKey: z.string().min(1).max(400),
  factor: z.number().finite(),
  /** De onde veio o fator do cômodo e a foto da média do tipo (Fase 40, RN-527). */
  origin: z.enum(['MEDIA', 'DIGITADO']).optional(),
  average: z
    .object({
      rate: z.number().finite().positive(),
      at: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data da média inválida.'),
      projects: z.number().int().min(0),
      rooms: z.number().int().min(0),
      roomKind: roomKindSchema,
    })
    .optional(),
});

export const roomInputSchema = z.object({
  id: idSchema,
  unitTypeId: idSchema,
  name: nonEmptyString('Nome do ambiente', 160),
  code: optionalTrimmed(30),
  kind: roomKindSchema,
  width: medida,
  length: medida,
  ceilingHeight: medida,
  /** Menos de três pontos não fecha forma nenhuma, e o domínio já recusa (ADR-114). */
  outline: z.array(pontoSchema).min(3).max(500).optional(),
  /**
   * Os lados do contorno que não são parede (D25). Faltando aqui, o zod os descartaria no
   * caminho e a varanda voltaria da recarga fechada por parede nenhuma — sem o guarda-corpo
   * e com o perímetro cheio. O teto do índice é o do contorno.
   */
  edgesWithoutWall: z
    .array(
      z.discriminatedUnion('kind', [
        z.object({
          edgeIndex: z.number().int().min(0).max(499),
          kind: z.literal('GUARDA_CORPO'),
          height: medida,
        }),
        z.object({ edgeIndex: z.number().int().min(0).max(499), kind: z.literal('ABERTO') }),
      ]),
    )
    .max(500)
    .optional(),
  areaOverride: medida.optional(),
  perimeterOverride: medida.optional(),
  notes: optionalTrimmed(500),
  /**
   * O fator próprio de cada item neste cômodo, nos serviços que descem a cômodo (Fase 39,
   * RN-512). Ausente, o servidor mantém o gravado; a lista vazia é "nenhum".
   */
  factors: z.array(itemFactorInputSchema).max(500).optional(),
});

/** O dono da parede e do elemento: a tipologia, ou o pavimento tipo quando é divisa. */
const ownerSchema = z.union([
  z.object({ kind: z.literal('UNIT_TYPE'), unitTypeId: idSchema }),
  z.object({ kind: z.literal('FLOOR_PLAN'), floorPlanId: idSchema }),
]);

/**
 * O trecho do lado que a parede ocupa, em fração do lado (RN-304). Começa antes de acabar,
 * e os dois dentro do lado — o mesmo que o `CHECK` da `0047` confere.
 */
const spanSchema = z
  .object({ start: z.number().min(0).max(1), end: z.number().min(0).max(1) })
  .refine((span) => span.start < span.end, 'O trecho da parede começa antes de acabar.');

const sideSchema = z
  .object({
    roomId: idSchema,
    positionId: idSchema.optional(),
    /**
     * O lado do contorno em que a parede nasceu (RN-243). Faltando aqui, o zod o descartaria
     * no caminho — como descartava a posição da peça — e a parede voltaria da recarga sem
     * lado, procurada de novo pela medida. O teto é o do contorno: 500 pontos, 500 lados.
     */
    edgeIndex: z.number().int().min(0).max(499).optional(),
    /**
     * Que trecho do lado ela ocupa (RN-304). Faltando aqui, os três pedaços da sala com o
     * quarto encostado no meio voltariam da recarga ocupando o lado inteiro, um sobre o outro.
     */
    span: spanSchema.optional(),
  })
  .refine(
    (side) => side.span === undefined || side.edgeIndex !== undefined,
    'O trecho da parede é de um lado do contorno — sem o lado, ele não diz nada.',
  );

export const wallInputSchema = z.object({
  id: idSchema,
  owner: ownerSchema,
  sideA: sideSchema,
  /** Ausente é a parede externa: do outro lado é a rua (RN-159). */
  sideB: sideSchema.optional(),
  length: medida,
  height: medida,
  thickness: medida.optional(),
  /** O guarda-corpo sobre a mureta, pela altura dele (D25). Ausente é parede sem guarda-corpo. */
  railingHeight: medida.optional(),
  joined: z.boolean(),
  /** O nome que alguém deu à parede — ausente é o comum, e a tela usa os ambientes. */
  label: optionalTrimmed(160),
  notes: optionalTrimmed(500),
});

export const openingInputSchema = z.object({
  id: idSchema,
  wallId: idSchema,
  kind: openingKindSchema,
  width: medida,
  height: medida,
  count: z.number().int().min(1).max(100),
  /** Onde o vão foi posto na parede, pela prancha. Ausente é o palpite distribuído. */
  offset: medidaOuZero.optional(),
});

/** A bitola do aço — a mesma lista fechada da taxa da disciplina (`STEEL_GAUGES`). */
const bitolaDaEstacaSchema = doDominio<SteelGauge>(STEEL_GAUGES.map((info) => info.code));

/**
 * A taxa de aço de uma peça ou de um tipo (Fase 41, etapa 2 — RN-543, RN-544; Fase 42, etapa 2 —
 * RN-567, RN-568): kg/m³ por bitola, a lista inteira. Taxa não positiva e bitola repetida são
 * recusadas no domínio (`normalizeSteelRates`), como a da disciplina. Ausente é "não disse";
 * vazia apaga.
 */
const taxaDaPecaSchema = z
  .array(z.object({ gauge: bitolaDaEstacaSchema, kgPerM3: z.number().finite() }))
  .max(20);

export const structuralElementInputSchema = z.object({
  id: idSchema,
  owner: ownerSchema,
  kind: elementKindSchema,
  name: optionalTrimmed(160),
  sectionWidth: medida,
  sectionDepth: medida,
  length: medida,
  /** Zero é a viga sem vazio nenhum, e é o comum — por isso não é `medida`. */
  freeSpan: medidaOuZero.optional(),
  /**
   * O contorno do pilar ou da viga na planta. Faltando aqui, o zod o descartaria no caminho
   * e a peça voltaria da recarga só com a seção. Menos de três pontos não fecha forma, e o
   * domínio confere o resto (`checkElementOutline`).
   */
  outline: z.array(pontoSchema).min(3).max(500).optional(),
  /** O tipo do pilar ou da viga (Fase 42, RN-562). Do mesmo elemento — o domínio confere. */
  elementTypeId: idSchema.optional(),
  /** O fator próprio do card (Fase 42, RN-563). Ausente: fica o gravado. */
  factors: z.array(itemFactorInputSchema).max(500).optional(),
  /** A taxa de aço própria do pilar ou da viga (Fase 42, RN-568). Ausente: fica a gravada. */
  steelRates: taxaDaPecaSchema.optional(),
  notes: optionalTrimmed(500),
});

export const elementSegmentInputSchema = z.object({
  id: idSchema,
  elementId: idSchema,
  wallId: idSchema,
  orientation: orientationSchema,
  length: medida.optional(),
  /**
   * Ausente é o avanço **derivado** da seção menos a espessura da parede; zero é rente
   * declarado (RN-187). Os dois atravessam a rede, e a diferença entre eles é a regra.
   */
  advance: medidaOuZero.optional(),
  advanceSide: advanceSideSchema.optional(),
  /**
   * Onde a peça está na parede: `offset` ao longo dela, `lateral` para o lado. Faltavam
   * aqui, e o zod os descartava no caminho — o servidor gravava a peça sem posição, e ao
   * reabrir a obra ela voltava ao meio da parede.
   */
  offset: medidaOuZero.optional(),
  lateral: z.number().finite().optional(),
});

/**
 * O shaft, a passagem na laje (Fase 24, D21): o ambiente em que está e os dois cantos do
 * retângulo, em metros, no sistema da tipologia — o mesmo do contorno do ambiente. O
 * retângulo sem largura é recusado no domínio (`createShaft`), onde a regra já mora.
 */
export const shaftInputSchema = z.object({
  id: idSchema,
  roomId: idSchema,
  name: optionalTrimmed(160),
  from: pontoSchema,
  to: pontoSchema,
  count: z.number().int().min(1).max(100),
  notes: optionalTrimmed(500),
});

// ── As peças da fundação (Fase 27, etapa 1b) ────────────────────────────────────

/**
 * O tipo de estaca da obra (D1): nome, diâmetro e comprimento em metros, e os kg de cada
 * bitola em uma estaca. Nome vazio, medida não positiva e bitola repetida são recusados no
 * domínio (`checkPileTypeFields`), onde a regra já mora.
 */
/** A foto da média do diâmetro (Fase 39, D8): o valor, o dia e de onde veio. */
const fotoDaMediaSchema = z.object({
  rate: z.number().finite().positive(),
  at: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data da média inválida.'),
  projects: z.number().int().min(0),
  piles: z.number().int().min(0),
});

const origemDaComposicaoSchema = z.enum(['MEDIA', 'DIGITADO']);

export const pileTypeInputSchema = z.object({
  id: idSchema,
  name: z.string().max(80),
  diameter: z.number().finite(),
  length: z.number().finite(),
  steel: z
    .array(
      z.object({
        gauge: bitolaDaEstacaSchema,
        kgPerPile: z.number().finite(),
        origin: origemDaComposicaoSchema.optional(),
        average: fotoDaMediaSchema.optional(),
      }),
    )
    .max(20),
  /**
   * A composição do tipo (Fase 39, RN-504): o fator de cada item dos serviços que medem a estaca.
   * Ausente, o servidor mantém a gravada — a tela de antes da Fase 39 não a apaga sem saber.
   */
  rates: z
    .array(
      z.object({
        serviceKey: z.string().min(1).max(400),
        itemKey: z.string().min(1).max(400),
        rate: z.number().finite(),
        origin: origemDaComposicaoSchema,
        average: fotoDaMediaSchema.optional(),
      }),
    )
    .max(500)
    .optional(),
});

/** O centro da peça, em metros, no sistema da tipologia — com a folga do CHECK da `0054`. */
const centroDaPecaSchema = z.object({
  x: z.number().finite().gt(-1e5).lt(1e5),
  y: z.number().finite().gt(-1e5).lt(1e5),
});

/**
 * A peça desenhada — por enquanto, só a estaca (F1, RN-360). Sem `center` é "a posicionar"
 * (D5). O nome, o tipo e o comprimento corrigido são conferidos no domínio
 * (`checkPileFields`, `checkLengthOverride`).
 */
export const structuralPieceInputSchema = z.object({
  id: idSchema,
  unitTypeId: idSchema,
  kind: z.literal('ESTACA'),
  name: z.string().max(160),
  pileTypeId: idSchema,
  center: centroDaPecaSchema.optional(),
  lengthOverride: z.number().finite().optional(),
  lengthReason: optionalTrimmed(500),
  notes: optionalTrimmed(500),
  /**
   * O fator próprio de cada item nesta estaca (Fase 39, RN-511). Ausente, o servidor mantém o
   * gravado — a tela de antes da etapa 3 não o apaga sem saber; a lista vazia é "nenhum".
   */
  factors: z.array(itemFactorInputSchema).max(500).optional(),
});

/**
 * A estaca marcada para um serviço (D4), pela **chave do catálogo** (`serviceKey`) — a mesma
 * do rascunho. O servidor a traduz para o id do serviço que a obra alcança.
 */
export const pieceMarkInputSchema = z.object({
  pieceId: idSchema,
  serviceKey: z.string().min(1).max(400),
});

/**
 * O bloco (Fase 27, etapa 2 — D3): o contorno em metros, no sistema da tipologia, e a
 * altura. Os pontos têm a folga do CHECK da `0055`; contorno de menos de três pontos ou sem
 * área, nome vazio ou repetido e altura não positiva são recusados no domínio
 * (`checkPileCapFields`), onde a regra já mora.
 */
export const pileCapInputSchema = z.object({
  id: idSchema,
  unitTypeId: idSchema,
  kind: z.literal('BLOCO'),
  name: z.string().max(160),
  outline: z.array(centroDaPecaSchema).max(500),
  height: z.number().finite(),
  /** O tipo de bloco (Fase 41, RN-537). Ausente: o bloco segue a obra. */
  pileCapTypeId: idSchema.optional(),
  /**
   * O fator próprio de cada item no bloco (Fase 41, RN-538). Ausente é "não disse": fica o
   * gravado; vazio apaga.
   */
  factors: z.array(itemFactorInputSchema).max(500).optional(),
  /** A taxa de aço própria do bloco (Fase 41, RN-544). */
  steelRates: taxaDaPecaSchema.optional(),
  notes: optionalTrimmed(500),
});

/**
 * A linha da composição de um tipo de bloco, de pilar ou de viga, com a origem e a foto da média
 * (Fase 42, etapa 4 — RN-582, RN-583; ADR-191, `0099`). Fator não positivo e item repetido são
 * recusados no domínio.
 */
const linhaDoTipoSchema = z.object({
  serviceKey: z.string().min(1).max(400),
  itemKey: z.string().min(1).max(400),
  factor: z.number().finite(),
  origin: origemDaComposicaoSchema.optional(),
  typeAverage: fotoDaMediaSchema
    .omit({ piles: true })
    .extend({ pieces: z.number().int().min(0) })
    .optional(),
});

/**
 * A taxa de aço de um tipo de bloco, de pilar ou de viga: a da peça (`taxaDaPecaSchema`), com a
 * origem e a foto da média de cada bitola (Fase 42, etapa 4 — ADR-191, `0099`).
 */
const taxaDoTipoSchema = z
  .array(
    z.object({
      gauge: bitolaDaEstacaSchema,
      kgPerM3: z.number().finite(),
      origin: origemDaComposicaoSchema.optional(),
      average: fotoDaMediaSchema.omit({ piles: true }).optional(),
    }),
  )
  .max(20);

/**
 * O tipo de bloco (Fase 41, etapa 1 — RN-536): o nome e a composição. Nome vazio, longo ou
 * repetido e fator não positivo são recusados no domínio (`checkPileCapTypeFields`).
 */
export const pileCapTypeInputSchema = z.object({
  id: idSchema,
  name: z.string().max(160),
  rates: z.array(linhaDoTipoSchema).max(500),
  /** A taxa de aço do tipo (Fase 41, RN-543), com a origem e a foto (ADR-191). Ausente: fica a gravada. */
  steelRates: taxaDoTipoSchema.optional(),
});

/**
 * O tipo de pilar e de viga (Fase 42, etapa 1 — RN-561): o elemento, o nome e a composição. Nome
 * vazio, longo ou repetido no elemento e fator não positivo são recusados no domínio
 * (`checkElementTypeFields`).
 */
export const elementTypeInputSchema = z.object({
  id: idSchema,
  kind: elementKindSchema,
  name: z.string().max(160),
  rates: z.array(linhaDoTipoSchema).max(500),
  /** A taxa de aço do tipo (Fase 42, RN-567), com a origem e a foto (ADR-191). Ausente: fica a gravada. */
  steelRates: taxaDoTipoSchema.optional(),
});

/**
 * A viga baldrame (Fase 27, etapa 2 — D6): o eixo de dois pontos e a seção, "20 × 40" como
 * largura 0,20 e altura 0,40. Eixo curto demais e seção não positiva são do domínio
 * (`checkGroundBeamFields`).
 */
export const groundBeamInputSchema = z.object({
  id: idSchema,
  unitTypeId: idSchema,
  kind: z.literal('BALDRAME'),
  name: z.string().max(160),
  from: centroDaPecaSchema,
  to: centroDaPecaSchema,
  width: z.number().finite(),
  height: z.number().finite(),
  /** O fator próprio de cada item na viga (Fase 41, RN-538). Ausente é "não disse". */
  factors: z.array(itemFactorInputSchema).max(500).optional(),
  /** A taxa de aço própria da viga (Fase 41, RN-544). */
  steelRates: taxaDaPecaSchema.optional(),
  notes: optionalTrimmed(500),
});

/**
 * O radier e a laje (Fase 27, etapa 3 — D7): o contorno em metros, no sistema da tipologia,
 * a espessura e as regiões de espessura diferente. Os pontos têm a folga do CHECK da
 * `0056`; nome vazio ou repetido, contorno sem área, espessura não positiva, região fora
 * do contorno ou sobrepondo outra são recusados no domínio (`checkSlabFields`, RN-369).
 */
export const slabInputSchema = z.object({
  id: idSchema,
  unitTypeId: idSchema,
  kind: z.enum(['RADIER', 'LAJE']),
  name: z.string().max(160),
  outline: z.array(centroDaPecaSchema).max(500),
  thickness: z.number().finite(),
  regions: z
    .array(
      z.object({
        outline: z.array(centroDaPecaSchema).max(500),
        thickness: z.number().finite(),
      }),
    )
    .max(200),
  /** O fator próprio de cada item no radier (Fase 41, RN-538). Ausente é "não disse". */
  factors: z.array(itemFactorInputSchema).max(500).optional(),
  /** A taxa de aço própria do radier (Fase 41, RN-544). */
  steelRates: taxaDaPecaSchema.optional(),
  notes: optionalTrimmed(500),
});

/**
 * O muro de contenção e a cortina (Fase 27, etapa 4 — D9): desenhados como a parede, dois
 * pontos, altura e espessura. Eixo curto demais e medida não positiva são do domínio
 * (`checkRetainingWallFields`).
 */
export const retainingWallInputSchema = z.object({
  id: idSchema,
  unitTypeId: idSchema,
  kind: z.literal('MURO_CONTENCAO'),
  name: z.string().max(160),
  from: centroDaPecaSchema,
  to: centroDaPecaSchema,
  height: z.number().finite(),
  thickness: z.number().finite(),
  /** O fator próprio de cada item no muro (Fase 42, RN-571). Ausente é "não disse"; vazio apaga. */
  factors: z.array(itemFactorInputSchema).max(500).optional(),
  /** A taxa de aço própria no muro (Fase 42, RN-572). Ausente: fica a gravada; vazia apaga. */
  steelRates: taxaDaPecaSchema.optional(),
  notes: optionalTrimmed(500),
});

/**
 * O pilar de contenção (Fase 27, etapa 4 — D9, F6): centro, seção e altura, com o ângulo do
 * muro em que está encaixado (solto, 0). Seção ou altura não positiva é do domínio
 * (`checkRetainingColumnFields`), que também leva o ângulo para [0, 360).
 */
export const retainingColumnInputSchema = z.object({
  id: idSchema,
  unitTypeId: idSchema,
  kind: z.literal('PILAR_CONTENCAO'),
  name: z.string().max(160),
  center: centroDaPecaSchema,
  sectionWidth: z.number().finite(),
  sectionDepth: z.number().finite(),
  height: z.number().finite(),
  angle: z.number().finite().optional(),
  /** O fator próprio de cada item no pilar de contenção (Fase 42, RN-571). Ausente é "não disse"; vazio apaga. */
  factors: z.array(itemFactorInputSchema).max(500).optional(),
  /** A taxa de aço própria no pilar de contenção (Fase 42, RN-572). Ausente: fica a gravada; vazia apaga. */
  steelRates: taxaDaPecaSchema.optional(),
  notes: optionalTrimmed(500),
});

/**
 * A escada (Fase 27, etapa 5 — D10): o contorno de cada lance e de cada patamar e, no lance,
 * os degraus, o espelho, o piso e a espessura da laje inclinada. Contorno sem área, lance sem
 * degrau e medida não positiva são do domínio (`checkStairFields`).
 */
export const stairInputSchema = z.object({
  id: idSchema,
  unitTypeId: idSchema,
  kind: z.literal('ESCADA'),
  name: z.string().max(160),
  flights: z
    .array(
      z.object({
        outline: z.array(centroDaPecaSchema).max(500),
        steps: z.number().finite(),
        riser: z.number().finite(),
        tread: z.number().finite(),
        thickness: z.number().finite(),
      }),
    )
    .max(50),
  landings: z
    .array(
      z.object({
        outline: z.array(centroDaPecaSchema).max(500),
        thickness: z.number().finite(),
      }),
    )
    .max(50),
  /** O fator próprio de cada item na escada (Fase 42, RN-571). Ausente é "não disse"; vazio apaga. */
  factors: z.array(itemFactorInputSchema).max(500).optional(),
  /** A taxa de aço própria na escada (Fase 42, RN-572). Ausente: fica a gravada; vazia apaga. */
  steelRates: taxaDaPecaSchema.optional(),
  notes: optionalTrimmed(500),
});

export const saveProjectGeometrySchema = z.object({
  projectId: idSchema,
  /**
   * A revisão do desenho sobre a qual quem grava trabalhou — a que `GetProjectGeometry`
   * devolveu, ou a que a gravação anterior devolveu. Se vier e não for a atual, a gravação
   * é recusada (409 `TAKEOFF.GEOMETRY_CONFLICT`) sem escrever nada: a aba desatualizada não
   * apaga em silêncio o que outra gravou. Ausente é o cliente de antes da revisão, que
   * segue como sempre: o último a gravar vence.
   */
  baseRevision: z.number().int().min(0).optional(),
  rooms: z.array(roomInputSchema).max(2000),
  walls: z.array(wallInputSchema).max(8000),
  openings: z.array(openingInputSchema).max(8000),
  elements: z.array(structuralElementInputSchema).max(4000),
  segments: z.array(elementSegmentInputSchema).max(8000),
  /**
   * Os tipos de pilar e de viga da obra (Fase 42, RN-561). Viajam com o desenho, como os de bloco,
   * porque o pilar e a viga os apontam. Ausente é "não disse"; presente substitui.
   */
  elementTypes: z.array(elementTypeInputSchema).max(1000).optional(),
  /**
   * Ausente é "não disse", e não "nenhum" (RN-272): a tela de antes do shaft, aberta numa
   * aba esquecida, grava o desenho sem saber que ele existe — e não pode apagá-lo por isso.
   * Presente, substitui como o resto do envelope.
   */
  shafts: z.array(shaftInputSchema).max(2000).optional(),
  /**
   * Os tipos de estaca da obra (Fase 27, D1). São da obra, e não da tipologia — mas viajam
   * com o desenho porque a estaca os aponta, e a gravação dos dois numa transação só é o
   * que deixa conferir "tipo em uso não se exclui" sem corrida. Ausente é "não disse".
   */
  pileTypes: z.array(pileTypeInputSchema).max(500).optional(),
  /** As peças — por enquanto, as estacas (Fase 27). Ausente é "não disse", como o shaft. */
  pieces: z.array(structuralPieceInputSchema).max(8000).optional(),
  /** As marcas por serviço (Fase 27, D4). Ausente é "não disse". */
  pieceMarks: z.array(pieceMarkInputSchema).max(16000).optional(),
  /**
   * Os tipos de bloco da obra (Fase 41, RN-536). Viajam com o desenho, como os de estaca, porque
   * o bloco os aponta. Ausente é "não disse"; presente substitui.
   */
  pileCapTypes: z.array(pileCapTypeInputSchema).max(500).optional(),
  /** Os blocos (Fase 27, etapa 2). Ausente é "não disse", como a estaca. */
  pileCaps: z.array(pileCapInputSchema).max(4000).optional(),
  /** As vigas baldrame (Fase 27, etapa 2). Ausente é "não disse". */
  groundBeams: z.array(groundBeamInputSchema).max(4000).optional(),
  /** O radier e a laje (Fase 27, etapa 3). Ausente é "não disse", como o bloco. */
  slabs: z.array(slabInputSchema).max(2000).optional(),
  /** O muro e o pilar de contenção (Fase 27, etapa 4). Ausente é "não disse", como o bloco. */
  retainingWalls: z.array(retainingWallInputSchema).max(4000).optional(),
  retainingColumns: z.array(retainingColumnInputSchema).max(4000).optional(),
  /** A escada (Fase 27, etapa 5). Ausente é "não disse", como o bloco. */
  stairs: z.array(stairInputSchema).max(500).optional(),
});
export type SaveProjectGeometryRequest = z.infer<typeof saveProjectGeometrySchema>;

// ── O cadastro de serviços ──────────────────────────────────────────────────────

/**
 * O catálogo da obra num envelope, como a geometria (Fase 14, fatia 4).
 *
 * Mesma forma e pelo mesmo motivo: disciplina, serviço e a medida de cada um valem juntos,
 * e gravá-los um a um faria a tela mandar quarenta requisições para uma tarde de decisões.
 *
 * A diferença é que aqui **não há substituição**: o envelope acrescenta e atualiza, e não
 * apaga o que ficou de fora. Serviço pode ter quantitativo lançado, e sumir por ausência
 * num envelope é destruição silenciosa — remover serviço é ação com nome próprio, e ela
 * chega quando a linha chegar.
 *
 * Não há id: a planilha não tem nenhum. O que casa o envelope com o que está gravado é a
 * chave natural `(disciplina, nome)` — a mesma da RN-164, e a mesma do índice único.
 */

/**
 * O elemento viaja ao lado da medida (Fase 24). Ausente é "não disse", e não "nenhum": o
 * caso de uso mantém o gravado ou usa o implícito da grandeza (`resolveAppliesTo`). O par é
 * conferido lá, e não aqui — a tabela de pares é do domínio (RN-256).
 */
const appliesToSchema = doDominio<AppliesTo>(Object.keys(APPLIES_TO_LABEL));

export const catalogServiceSchema = z.object({
  name: nonEmptyString('Nome do serviço', 200),
  code: optionalTrimmed(30),
  measureBy: doDominio<MeasureBy>(Object.keys(MEASURE_BY_LABEL)),
  appliesTo: appliesToSchema.optional(),
  /** Metros. Ausente não é zero: é serviço que não tem espessura nenhuma. */
  layerThickness: medida.optional(),
  /**
   * Metros para cada lado do vão — a soleira e o peitoril passam dele (RN-308). Zero vale, e é
   * o mesmo que ausente; negativo é recusado: encurtaria a pedra para dentro do vão.
   */
  sideMargin: medidaOuZero.optional(),
});

export const applyServiceCatalogSchema = z.object({
  projectId: idSchema,
  disciplines: z
    .array(
      z.object({
        name: nonEmptyString('Nome da disciplina', 160),
        services: z.array(catalogServiceSchema).max(500),
      }),
    )
    .max(100),
});
export type ApplyServiceCatalogRequest = z.infer<typeof applyServiceCatalogSchema>;

// ── O cadastro global do serviço (Fase 21, fatia 1) ─────────────────────────────

/**
 * O serviço entra pelo cadastro, um a um — e não por envelope.
 *
 * O envelope acima existe porque a planilha chega inteira; aqui é alguém digitando em
 * Configurações → Serviços, e uma tela de cadastro que mandasse a lista toda a cada
 * gravação apagaria o que outra pessoa cadastrou entre a leitura e o salvar.
 *
 * Não há `projectId`: este cadastro é global por construção (ADR-134). A obra consome, não
 * cadastra — e um campo opcional de obra aqui reabriria, por acidente, exatamente o que
 * esta fase fecha.
 */
export const createGlobalServiceSchema = z.object({
  disciplineId: idSchema,
  name: nonEmptyString('Nome do serviço', 200),
  code: optionalTrimmed(30),
  measureBy: doDominio<MeasureBy>(Object.keys(MEASURE_BY_LABEL)),
  appliesTo: appliesToSchema.optional(),
  /** Metros. Ausente não é zero: é serviço que não tem espessura nenhuma. */
  layerThickness: medida.optional(),
  /** Metros para cada lado do vão (RN-308). Zero ou ausente é a soleira rente ao vão. */
  sideMargin: medidaOuZero.optional(),
  /** Ausente = serviço ainda sem unidade; quem for medir nele é recusado (RN-232). */
  unitOfMeasureId: idSchema.optional(),
});
export type CreateGlobalServiceRequest = z.infer<typeof createGlobalServiceSchema>;

/**
 * Edição do serviço já cadastrado — o nome e a disciplina incluídos.
 *
 * O nome é a chave natural (RN-230) e ainda assim é editável: corrigir a grafia é edição,
 * e as quantidades e verificações já gravadas seguem apontando para o mesmo id. A
 * disciplina também entra, porque serviço arquivado na gaveta errada precisa de conserto,
 * e não de um segundo cadastro.
 *
 * Os opcionais ausentes **limpam** o campo. Espessura em branco não é zero, é serviço que
 * não mede em m³ nenhum — e quem apagou o campo na tela quis dizer isso.
 */
export const updateGlobalServiceSchema = createGlobalServiceSchema.extend({
  serviceId: idSchema,
});
export type UpdateGlobalServiceRequest = z.infer<typeof updateGlobalServiceSchema>;

/** Liga e desliga o serviço no catálogo — desligar não apaga o que já foi medido (RN-234). */
export const setGlobalServiceActiveSchema = z.object({
  serviceId: idSchema,
  isActive: z.boolean(),
});
export type SetGlobalServiceActiveRequest = z.infer<typeof setGlobalServiceActiveSchema>;

// ── Os itens do serviço (Fase 24, fatia 3) ──────────────────────────────────────

/**
 * A outra metade do catálogo num envelope: o que cada serviço consome, a planilha de onde
 * veio e o que cada tipologia decidiu por cima dela (RN-249).
 *
 * **Aqui há substituição**, ao contrário de `applyServiceCatalogSchema`. Lá o serviço pode
 * ter quantitativo lançado, e sumir por ausência seria destruição silenciosa; aqui o
 * envelope é o retrato inteiro dos itens da obra, e o item que a tela não manda mais é o
 * item que alguém tirou (RN-255). O serviço sem item nenhum, sem aba e sem unidade não
 * precisa vir: ausente e vazio dizem a mesma coisa.
 *
 * Sem id, como o outro envelope: o serviço é achado pela chave natural `(disciplina,
 * nome)` e o item pela descrição normalizada (RN-251). A quantidade digitada vem
 * **pendurada no item**, e não numa lista à parte com chave de texto — o item novo só
 * ganha id do lado de lá, e a quantidade dele precisa achá-lo na mesma gravação.
 */

/** As bitolas da lista fechada (Fase 27, `STEEL_GAUGES`) — o que a siderúrgica vende. */
const steelGaugeSchema = doDominio<SteelGauge>(STEEL_GAUGES.map((info) => info.code));

/**
 * As taxas de aço de uma disciplina, nesta obra (Fase 27, RN-367): os kg de cada bitola por
 * m³ de concreto da peça. Positiva — a bitola que a disciplina não usa não tem linha.
 */
const steelRatesSchema = z
  .array(
    z.object({
      gauge: steelGaugeSchema,
      kgPerM3: z
        .number()
        .finite()
        .positive('A taxa de aço deve ser maior que zero.')
        .max(999_999_999, 'Taxa de aço grande demais.'),
      /** De onde veio a taxa, e a foto da média (Fase 40, etapa 3 — RN-533). */
      origin: origemDaComposicaoSchema.optional(),
      average: fotoDaMediaSchema.omit({ piles: true }).optional(),
    }),
  )
  .max(20);

/** Quanto cada aba da planilha declarou, pelo nome da aba. */
const quantidadesPorAbaSchema = z
  .record(z.string().max(120), z.number().finite())
  .refine((porAba) => Object.keys(porAba).length <= 200, 'Abas demais numa quantidade.');

/**
 * A quantidade que a tipologia decidiu. Zero é aceito — é "aqui não acontece" (RN-253).
 *
 * O teto é o da coluna (`numeric(14, 6)`): acima dele o banco recusaria com erro cru, e a
 * tela ficaria sem saber qual dos duzentos campos estourou.
 */
const quantidadeDigitadaSchema = z
  .number()
  .finite()
  .min(0, 'A quantidade não pode ser negativa. Zero é decidir que o item não acontece aqui.')
  .max(99_999_999, 'Quantidade grande demais.');

/**
 * A quantidade pronta de uma peça importada (Fase 28, RN-384) — no item ou no serviço.
 *
 * Pelo **nome** da peça, como a planilha por peça a escreve ("B24", "E06-A - R40"): a peça
 * importada ainda não tem desenho, e é o nome que a liga à peça que um dia for posicionada.
 */
export const pieceQuantityInputSchema = z.object({
  unitTypeId: idSchema,
  pieceName: nonEmptyString('Nome da peça', 120),
  quantity: quantidadeDigitadaSchema,
});

export const serviceItemInputSchema = z.object({
  description: nonEmptyString('Descrição do item', 300),
  code: optionalTrimmed(60),
  unit: optionalTrimmed(30),
  payment: optionalTrimmed(60),
  kind: doDominio<ItemKind>(Object.keys(ITEM_KIND_LABEL)),
  kindOrigin: z.enum(['PALPITE', 'DECIDIDO']),
  /** Só no item de mão de obra, e só uma função da disciplina do serviço (RN-252). */
  jobRoleId: idSchema.optional(),
  quantityBySheet: quantidadesPorAbaSchema,
  /**
   * O índice de consumo e a perda (Fase 24, fatia 6): quanto do item por unidade do medido
   * do serviço, e quanto se perde dele, em por cento. Do cadastro — valem para todas as
   * tipologias. Ausentes = sem índice.
   */
  consumptionIndex: z
    .number()
    .finite()
    .min(0, 'O índice de consumo não pode ser negativo.')
    .max(99_999_999, 'Índice grande demais.')
    .optional(),
  lossPercent: z
    .number()
    .finite()
    .min(0, 'A perda não pode ser negativa.')
    .max(999.999, 'A perda fica abaixo de 1000%.')
    .optional(),
  /**
   * De onde veio o índice e a foto da média (Fase 40, RN-520). Sem índice, são ignorados; a
   * `MEDIA` sem foto vira `DIGITADO` no domínio (`indexProvenance`).
   */
  indexOrigin: origemDaComposicaoSchema.optional(),
  indexAverage: fotoDaMediaSchema
    .omit({ piles: true })
    .extend({ unitTypes: z.number().int().min(0) })
    .optional(),
  /**
   * A bitola, quando o item é aço (Fase 27). Só no CORTE E DOBRA DE FERRAGEM, e nunca na mão
   * de obra — quem recusa é o domínio (RN-366), que sabe o nome do serviço.
   */
  steelGauge: steelGaugeSchema.optional(),
  /**
   * As tipologias que decidiram um número para este item. Ausente = segue o calculado, ou a
   * planilha. O motivo acompanha o número digitado por cima do calculado (D9).
   */
  quantities: z
    .array(
      z.object({
        unitTypeId: idSchema,
        quantity: quantidadeDigitadaSchema,
        reason: optionalTrimmed(500),
      }),
    )
    .max(500),
  /**
   * A quantidade pronta de cada peça importada (Fase 28, RN-384) — "B24 leva 33,18 kg". Só
   * vale para as tipologias de `pieceQuantityUnitTypes`; ausente = nenhuma peça traz este item.
   */
  pieceQuantities: z.array(pieceQuantityInputSchema).max(5000).optional(),
});

/**
 * O que a tipologia decidiu sobre o **serviço** (Fase 24, fatia 5): o medido digitado — a
 * correção com motivo, ou a quantidade do serviço que o desenho não mede — e a quantidade na
 * unidade do serviço, quando ela diverge da medida.
 *
 * Pendurada no serviço do envelope pelo mesmo motivo da quantidade do item: é o serviço que
 * a tela conhece pela chave natural, e o id dele só existe do lado de lá. O motivo é
 * conferido lá também, contra a peça que o **servidor** tem para o serviço (D4).
 */
const quantidadeDoServicoSchema = z
  .number()
  .finite()
  .min(0, 'A quantidade do serviço não pode ser negativa.')
  .max(99_999_999, 'Quantidade grande demais.');

const serviceQuantityInputSchema = z
  .object({
    unitTypeId: idSchema,
    measured: quantidadeDoServicoSchema.optional(),
    reason: optionalTrimmed(500),
    quantity: quantidadeDoServicoSchema.optional(),
  })
  .refine(
    (decisao) => decisao.measured !== undefined || decisao.quantity !== undefined,
    'A decisão do serviço precisa de um número: sem nenhum, ela é a ausência — segue o traçado.',
  );

const sheetSourceSchema = z.object({
  name: nonEmptyString('Nome da aba', 120),
  scope: doDominio<SheetScope>(Object.keys(SHEET_SCOPE_LABEL)),
  summary: z.object({
    disciplines: z.number().int().nonnegative(),
    services: z.number().int().nonnegative(),
    items: z.number().int().nonnegative(),
    codes: z.number().int().nonnegative(),
  }),
  imported: z.boolean(),
});

export const saveProjectServiceItemsSchema = z.object({
  projectId: idSchema,
  /** A planilha importada. Ausente = catálogo montado à mão, ou planilha removida. */
  sheetImport: z
    .object({
      fileName: optionalTrimmed(260),
      importedAt: z.string().datetime({ offset: true }).optional(),
      sources: z.array(sheetSourceSchema).max(200),
    })
    .optional(),
  disciplines: z
    .array(
      z.object({
        name: nonEmptyString('Nome da disciplina', 160),
        /**
         * As taxas de aço da disciplina (Fase 27, ADR-176). **Ausente não mexe** — a tela que
         * ainda não conhece o campo não apaga a taxa de ninguém; **vazio apaga** as taxas da
         * disciplina; com linhas, elas substituem as que havia.
         */
        steelRates: steelRatesSchema.optional(),
        /**
         * As listas por elemento, na disciplina que mistura elementos (Fase 42, RN-573): na
         * PILARES DE CONTENÇÃO, a do muro e a do pilar. **Ausente não mexe**; presente substitui
         * todas as listas por elemento da disciplina — a do elemento que não veio sai.
         */
        elementSteelRates: z
          .array(z.object({ element: appliesToSchema, rates: steelRatesSchema }))
          .max(20)
          .optional(),
        services: z
          .array(
            z.object({
              name: nonEmptyString('Nome do serviço', 200),
              /** A unidade em que a planilha mediu o serviço — "M²", "M". */
              unit: optionalTrimmed(30),
              quantityBySheet: quantidadesPorAbaSchema,
              items: z.array(serviceItemInputSchema).max(1000),
              /** O que cada tipologia decidiu sobre o serviço. Ausente = nenhuma decidiu. */
              quantities: z.array(serviceQuantityInputSchema).max(500).optional(),
              /** A quantidade do serviço em cada peça importada (Fase 28), na unidade dele. */
              pieceQuantities: z.array(pieceQuantityInputSchema).max(5000).optional(),
            }),
          )
          .max(1000),
      }),
    )
    .max(200),
  /**
   * As tipologias que este envelope descreve, com a aba de referência de cada uma.
   *
   * É o **escopo** da quantidade digitada: a tipologia listada aqui tem as decisões
   * trocadas pelas que vieram nos itens; a que ficou de fora não é tocada.
   */
  unitTypes: z.array(z.object({ unitTypeId: idSchema, sheetName: optionalTrimmed(120) })).max(500),
  /**
   * As tipologias cujas quantidades por peça este envelope descreve (Fase 28, RN-386).
   *
   * **Ausente não mexe** — a tela que ainda não conhece a planilha por peça não apaga o que
   * outra importou. Listada, a tipologia tem as quantidades por peça **trocadas** pelas que
   * vieram nos itens e serviços; a que tinha e não veio fica sem nenhuma. A peça de uma
   * tipologia que não está aqui é ignorada, e não gravada. A mesma peça duas vezes — tipologia,
   * serviço, item e nome (`nomeDaPeca`) iguais — recusa o envelope
   * (`TAKEOFF.PIECE_QUANTITY_DUPLICATED`): quem junta as linhas é a tela.
   */
  pieceQuantityUnitTypes: z.array(idSchema).max(500).optional(),
});
export type SaveProjectServiceItemsRequest = z.infer<typeof saveProjectServiceItemsSchema>;

/**
 * A unidade do serviço, trocada no quadro Serviços × traçado (Fase 24, D3).
 *
 * Uma unidade só por serviço, a do cadastro — a mesma da meta e da medição do planejamento.
 * Ausente é "sem unidade". A troca é recusada quando o serviço já tem medição gravada: os
 * números lançados não se converteriam sozinhos.
 */
export const setProjectServiceUnitSchema = z.object({
  projectId: idSchema,
  serviceId: idSchema,
  unitOfMeasureId: idSchema.optional(),
});
export type SetProjectServiceUnitRequest = z.infer<typeof setProjectServiceUnitSchema>;

/**
 * A sugestão de produtividade da mão de obra (Fase 24, D14).
 *
 * Quem pergunta diz a obra, o serviço e a função: a média que a obra — ou, sem dados
 * bastantes, a empresa — já mostrou para aquela função naquele serviço. É leitura, e não
 * grava nada: a sugestão só vira índice quando a tela a envia, pelos itens, como qualquer
 * índice digitado.
 */
export const getProductivitySuggestionSchema = z.object({
  projectId: idSchema,
  serviceId: idSchema,
  jobRoleId: idSchema,
});
export type GetProductivitySuggestionRequest = z.infer<typeof getProductivitySuggestionSchema>;

// ── O apontamento por peça (Fase 27, etapa 6) ───────────────────────────────────

/**
 * As peças da frente para o lançamento do dia (D21): a frente diz onde, o serviço diz que
 * peça, e a data diz quais já foram marcadas **neste** dia.
 */
export const getFrontPiecesSchema = z.object({
  projectId: idSchema,
  activityId: idSchema,
  serviceId: idSchema,
  date: businessDateSchema,
});

/**
 * De onde veio a medida da peça (RN-412): o número da planilha importada (RN-384) ou a conta do
 * desenho. A peça só da planilha, sem medida para o serviço, vem como `PLANILHA` sem `measure`.
 */
export type FrontPieceSource = 'PLANILHA' | 'DESENHO';

/**
 * Uma peça da frente, com o que marcá-la soma ao dia (RN-382).
 *
 * **A chave é o nome** (ADR-178, RN-413): "B93" é a mesma peça na planilha importada e no
 * desenho, e é por ele que a marca é gravada. A peça importada não precisa estar desenhada nem
 * posicionada para ser executada (RN-411).
 */
export interface FrontPieceView {
  /** "B93", "E06-A - R40" — único na área comum do desenho (RN-376). */
  readonly name: string;
  /** A peça desenhada com este nome, quando há. */
  readonly pieceId?: string;
  /** O tipo da peça desenhada; a só da planilha não tem. */
  readonly kind?: PieceKind;
  readonly source: FrontPieceSource;
  /** Ausente: nem a planilha nem o desenho dão número para este serviço — marca e não soma. */
  readonly measure?: number;
  /** AAAA-MM-DD em que a peça já foi executada neste serviço, em qualquer dia. */
  readonly executedOn?: string;
  /** Executada exatamente na data pedida — a caixa marcada da tela. */
  readonly executedHere: boolean;
  /**
   * A figura da peça na planta pintada (Fase 34, RN-448), nas coordenadas da prancha: a do
   * desenho, ou o ponto da peça da planilha posicionada. Ausente: a peça não tem lugar na planta.
   */
  readonly shape?: PieceShape;
}

export interface FrontPiecesView {
  /**
   * O tipo que o serviço mede no desenho, quando mede. Ausente não quer dizer lista vazia: a
   * planilha importada pode trazer peças para um serviço que o desenho não mede (o ARRANQUE).
   */
  readonly pieceKind?: PieceKind;
  /** O rótulo da área comum do desenho do pavimento, de onde as peças vêm. */
  readonly unitCode?: string;
  /**
   * O desenho de pavimento da frente (22/10/2026): a planta da execução põe a prancha dele por
   * baixo dos cômodos e das peças, na mesma escala em metros da bancada. Vem também sem peças.
   */
  readonly floorPlanId?: string;
  readonly pieces: readonly FrontPieceView[];
}

/**
 * As marcas do dia para esta frente × serviço (D22): a lista **substitui** as marcas desta
 * data. A quantidade do dia não vai aqui — a tela preenche o campo e grava pelo caminho de
 * sempre (D23).
 */
export const recordPieceExecutionsSchema = z.object({
  projectId: idSchema,
  activityId: idSchema,
  serviceId: idSchema,
  date: businessDateSchema,
  /** Os nomes das peças marcadas no dia (RN-413) — a lista substitui as marcas desta data. */
  pieceNames: z
    .array(z.string().trim().min(1, 'Nome de peça vazio.').max(160, 'Nome de peça longo demais.'))
    .max(2000, 'Peças demais num lançamento só.'),
  allocationId: idSchema.optional(),
});

export interface PieceExecutionsResult {
  readonly marked: number;
  readonly unmarked: number;
  /** A soma das medidas das peças marcadas nesta data (`executedFromPieces`). */
  readonly executed?: number;
}

// ── O avanço por cômodo (Fase 34, etapa 1) ───────────────────────────────────────

/**
 * Os cômodos da frente para um serviço (RN-441): os aptos do pavimento — as posições do desenho
 * dele, com a área comum (D12) — e os cômodos da tipologia de cada um. Com `date`, o percentual
 * que vale é o daquele dia; sem ela, o último.
 */
export const getFrontRoomsSchema = z.object({
  projectId: idSchema,
  activityId: idSchema,
  serviceId: idSchema,
  date: businessDateSchema.optional(),
});

/** Um cômodo de um apto do pavimento, com o peso dele e o percentual que vale. */
export interface FrontRoomView {
  /** A posição do desenho: o apto, ou a área comum. */
  readonly positionId: string;
  /** "01", "Área comum" — o que a planta chama; a tela diz "Apto 01 · 12º Pavimento". */
  readonly positionLabel: string;
  /** Quantas vezes a posição existe no pavimento — o peso do cômodo vai multiplicado. */
  readonly positionCount: number;
  readonly unitTypeId: string;
  readonly unitTypeName: string;
  readonly roomId: string;
  readonly roomName: string;
  readonly roomKind: RoomKind;
  /** A quantidade do serviço no cômodo pelo desenho (uma vez). Ausente: o desenho não o mediu. */
  readonly measure?: number;
  /** O peso no avanço do item (D9): zero no "não se aplica". */
  readonly weight: number;
  /** O cômodo da tipologia está fora da conta deste serviço (RN-444). */
  readonly notApplicable: boolean;
  /** O percentual que vale (RN-443). Ausente: ninguém apontou ainda. */
  readonly percent?: number;
  /** AAAA-MM-DD do apontamento que vale. */
  readonly recordedOn?: string;
  readonly recordedByUserId?: string;
  /**
   * O contorno do cômodo, nas coordenadas da prancha do pavimento — o mesmo de todos os aptos da
   * tipologia (Fase 34, etapa 2: a planta pintada, RN-448). Ausente: o cômodo não foi traçado.
   */
  readonly outline?: readonly Point[];
}

export interface FrontRoomsView {
  /** O serviço mede por ambiente e desce a cômodo (RN-442). Falso: lista vazia, e o avanço fica no pavimento. */
  readonly byRoom: boolean;
  /** De onde veio o peso: o desenho, ou igual para todos — o serviço sem desenho, que a tela avisa. */
  readonly basis?: RoomWeightBasis;
  /** O avanço do item pelos cômodos (RN-445). Ausente sem nenhum cômodo informado. */
  readonly progressFromRooms?: number;
  readonly rooms: readonly FrontRoomView[];
}

/**
 * O percentual do dia de alguns cômodos da frente × serviço (RN-443). Cada um é um apontamento
 * novo na trilha; o que repete o percentual que já valia naquele dia não grava nada.
 */
export const recordRoomProgressSchema = z.object({
  projectId: idSchema,
  activityId: idSchema,
  serviceId: idSchema,
  date: businessDateSchema,
  rooms: z
    .array(
      z.object({
        positionId: idSchema,
        roomId: idSchema,
        // A faixa é do domínio (`checkRoomPercent`), que responde com o código do cômodo.
        percent: z.number({ invalid_type_error: 'O percentual é um número.' }),
      }),
    )
    .min(1, 'Nenhum cômodo para apontar.')
    .max(2000, 'Cômodos demais num apontamento só.'),
});

export interface RoomProgressResult {
  /** Os apontamentos gravados. */
  readonly recorded: number;
  /** Os que repetiam o percentual que já valia no dia, e não gravaram nada. */
  readonly unchanged: number;
  /** O avanço do item pelos cômodos depois do apontamento (RN-445). */
  readonly progressFromRooms?: number;
}

/** Marca ou desmarca o "não se aplica" do cômodo da tipologia num serviço (RN-444). */
export const setRoomNotApplicableSchema = z.object({
  projectId: idSchema,
  serviceId: idSchema,
  roomId: idSchema,
  notApplicable: z.boolean(),
});

export interface RoomNotApplicableResult {
  /** Falso quando o cômodo já estava como pedido. */
  readonly changed: boolean;
}

// ── As peças da planilha na planta (Fase 28, etapa 3) ──────────────────────────────

const nomeDaPecaSchema = z
  .string()
  .trim()
  .min(1, 'Nome de peça vazio.')
  .max(160, 'Nome de peça longo demais.');

/** Um ponto da planta, em metros nas coordenadas da tipologia. O teto só barra lixo. */
const coordenadaSchema = z.number().finite('Coordenada inválida.').min(-100_000).max(100_000);

/** As peças importadas de uma tipologia, com o lugar e a execução de cada uma (RN-427 a RN-430). */
export const getImportedPiecesSchema = z.object({
  projectId: idSchema,
  unitTypeId: idSchema,
});

/** Posiciona — ou move — a peça importada na planta (RN-429). */
export const setImportedPiecePositionSchema = z.object({
  projectId: idSchema,
  unitTypeId: idSchema,
  pieceName: nomeDaPecaSchema,
  x: coordenadaSchema,
  y: coordenadaSchema,
});

/** Tira a peça da planta: ela volta para a fila, e a quantidade não muda. */
export const clearImportedPiecePositionSchema = z.object({
  projectId: idSchema,
  unitTypeId: idSchema,
  pieceName: nomeDaPecaSchema,
});

/**
 * Dá à peça importada um nome novo (20/10/2026, RN-457 a RN-460): todos os serviços dela, o
 * ponto na planta, as marcas de execução e a peça desenhada de mesmo nome. O teto é o da
 * quantidade por peça (`0064`).
 */
export const renameImportedPieceSchema = z.object({
  projectId: idSchema,
  unitTypeId: idSchema,
  pieceName: nomeDaPecaSchema,
  newName: z
    .string()
    .trim()
    .min(1, 'Digite o nome novo da peça.')
    .max(120, 'Nome de peça longo demais (até 120 caracteres).'),
});

/** O que o nome novo levou junto — a tela refaz o mesmo na mesa e diz o que mudou. */
export interface RenamedImportedPiece {
  readonly unitTypeId: string;
  /** Como a peça se chamava. */
  readonly previousName: string;
  /** Como ela se chama agora — o nome digitado, no formato da peça (maiúsculo, sem sobra). */
  readonly pieceName: string;
  /** Linhas da planilha renomeadas: um serviço ou item da peça cada. */
  readonly quantities: number;
  readonly position: boolean;
  /** Marcas de execução que foram junto (RN-459). */
  readonly executions: number;
  /** Peças desenhadas de mesmo nome que foram junto. */
  readonly drawnPieces: number;
  /** A lembrança dos nomes da tipologia depois do renomear (RN-458). */
  readonly aliases: readonly PieceNameAliasView[];
  /** A revisão nova do desenho, quando uma peça desenhada mudou de nome (`0062`). */
  readonly geometryRevision?: number;
}

/** "A planilha chama de B06 a peça que hoje se chama B06A" (RN-458). */
export interface PieceNameAliasView {
  readonly sheetName: string;
  readonly pieceName: string;
}

export interface ImportedPieceView {
  readonly name: string;
  readonly kind: ImportedPieceKind;
  /** Ausente: a peça ainda não tem lugar na planta — está na fila. */
  readonly position?: { readonly x: number; readonly y: number };
  readonly services: number;
  readonly executedServices: number;
  readonly lastExecutedOn?: string;
  readonly status: ImportedPieceStatus;
  /** Os nomes com que a planilha escreve a peça renomeada (RN-458). Ausente: nunca mudou. */
  readonly sheetNames?: readonly string[];
}

export interface ImportedPiecesView {
  readonly unitTypeId: string;
  readonly pieces: readonly ImportedPieceView[];
}
